import { ref, computed, watch } from '@vue/composition-api'
import store from '@/store'
import { format, subYears } from 'date-fns'
import {
  downloadBlob, maskCnpj, truncateString, formatCurrencyInt,
} from '@core/utils/utils'

export default function useMonthlyWorkplaceReport() {
  const now = new Date()
  const currentYear = format(now, 'yyyy')
  const currentMonth = format(now, 'MM')

  const filterYear = ref(currentYear)
  const filterMonth = ref(currentMonth)
  const institutionId = ref(null)
  const institutionOptions = ref([])

  const loading = ref(false)
  const items = ref([])

  const yearOptions = (new Array(4)).fill(0).map((_, i) => {
    const year = format(subYears(now, i), 'yyyy')
    return { label: year, value: year }
  }).filter(({ value }) => value > '2020')

  const allMonthOptions = [
    { label: 'Janeiro', value: '01' },
    { label: 'Fevereiro', value: '02' },
    { label: 'Março', value: '03' },
    { label: 'Abril', value: '04' },
    { label: 'Maio', value: '05' },
    { label: 'Junho', value: '06' },
    { label: 'Julho', value: '07' },
    { label: 'Agosto', value: '08' },
    { label: 'Setembro', value: '09' },
    { label: 'Outubro', value: '10' },
    { label: 'Novembro', value: '11' },
    { label: 'Dezembro', value: '12' },
  ]

  const monthOptions = computed(() => (
    filterYear.value === currentYear
      ? allMonthOptions.filter(({ value }) => value <= currentMonth)
      : allMonthOptions
  ))

  watch(filterYear, value => {
    if (value === currentYear && filterMonth.value > currentMonth) {
      filterMonth.value = currentMonth
    }
  })

  const tableColumns = [
    { key: 'institution_name', label: 'Instituição' },
    { key: 'workplace_name', label: 'Unidade' },
    { key: 'physicians_launched', label: 'Profissionais Lançado' },
    { key: 'launched_quantity', label: 'Lançamentos' },
    { key: 'anticipated_quantity', label: 'Antecipados' },
    { key: 'anticipated_3_month_average', label: 'Média Antec. 3 Meses' },
    { key: 'frequency', label: 'Frequência' },
    {
      key: 'launched_value', label: 'Valor Lançado', formatter: formatCurrencyInt,
    },
    {
      key: 'anticipated_value', label: 'Valor Antecipados', formatter: formatCurrencyInt,
    },
    {
      key: 'payroll_value', label: 'Valor Folha', formatter: formatCurrencyInt,
    },
  ]

  const getParams = () => ({
    competence: `${filterYear.value}-${filterMonth.value}`,
    institution: institutionId.value ? institutionId.value.value : undefined,
  })

  const fetchInstitutions = () => {
    store.dispatch('app-reports/fetchInstitutions', { perPage: 99999999 })
      .then(response => {
        institutionOptions.value = response.data.data.map(inst => ({
          value: inst.id,
          label: `${inst.name} | ${maskCnpj(inst.cnpj)}`,
        }))
      })
  }

  const loadReport = () => {
    loading.value = true
    store.dispatch('app-reports/monthlyWorkplaceReport', getParams())
      .then(response => {
        items.value = response.data.data ?? []
      })
      .finally(() => {
        loading.value = false
      })
  }

  const exporting = ref(false)
  const downloadReport = () => {
    exporting.value = true
    store.dispatch('app-reports/monthlyWorkplaceReportExport', getParams())
      .then(response => {
        downloadBlob(null, response)
      })
      .finally(() => {
        exporting.value = false
      })
  }

  return {
    loading,
    filterYear,
    filterMonth,
    yearOptions,
    monthOptions,
    institutionId,
    institutionOptions,
    fetchInstitutions,
    items,
    tableColumns,
    loadReport,
    downloadReport,
    exporting,
    truncateString,
  }
}
